

import { useState, useEffect } from "react";
import AddServiceModal from "./AddServiceModal";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
}

interface PackageService {
  id?: number;
  service_id: number;
  name: string;
  selling_price: number;
  base_price?: number;
  vendor_id?: number;
  unit_price?: number;
  cost_price?: number;
}

interface PackageFormData {
  id?: number;
  name: string;
  description: string;
  base_price: string;
  offered_price: string;
  services_data: PackageService[];
  cost_total?: string;
}

interface PackageModalProps {
  isOpen: boolean;
  initialData?: Partial<PackageFormData>;
  onSubmit: (data: PackageFormData) => void;
  onCancel: () => void;
  services: Service[];
  vendors: Vendor[];
}

interface Vendor {
  id: number;
  name: string;
}

export default function PackageModal({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  services,
  vendors,
}: PackageModalProps) {
  const [formData, setFormData] = useState<PackageFormData>({
    name: "",
    description: "",
    base_price: "0",
    offered_price: "0",
    services_data: [],
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PackageFormData, string>>>({});
  const [removeServiceConfirm, setRemoveServiceConfirm] = useState<number | null>(null);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);

  const toNumber = (v: unknown): number => {
    if (typeof v === 'number') return isNaN(v) ? 0 : v;
    if (typeof v === 'string') { const n = parseFloat(v); return isNaN(n) ? 0 : n; }
    return 0;
  };

  // On open: load data and compute prices from services immediately
  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      const svcList = initialData.services_data || [];
      const sellingTotal = svcList.reduce(
        (sum, s: any) => sum + toNumber(s.selling_price ?? s.unit_price), 0
      );
      const costTotal = svcList.reduce(
        (sum, s: any) => sum + toNumber(s.base_price ?? s.cost_price), 0
      );
      const computed = svcList.length > 0
        ? String(Math.round(sellingTotal))
        : String(toNumber(initialData.offered_price) || toNumber(initialData.base_price) || 0);

      setFormData({
        id: initialData.id,
        name: initialData.name || "",
        description: initialData.description || "",
        base_price: computed,
        offered_price: computed,
        services_data: svcList,
        cost_total: String(Math.round(costTotal)),
      });
    } else {
      setFormData({ name: "", description: "", base_price: "0", offered_price: "0", services_data: [] });
    }
    setErrors({});
  }, [isOpen, initialData]);

  // Realtime: recompute whenever services list changes
  useEffect(() => {
    if (formData.services_data.length === 0) return;

    const sellingTotal = formData.services_data.reduce(
      (sum, s) => sum + toNumber(s.selling_price ?? s.unit_price), 0
    );
    const costTotal = formData.services_data.reduce(
      (sum, s) => sum + toNumber(s.base_price ?? s.cost_price), 0
    );
    const total = String(Math.round(sellingTotal));

    setFormData(prev => ({
      ...prev,
      base_price: total,
      offered_price: total,
      cost_total: String(Math.round(costTotal)),
    }));
  }, [formData.services_data]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PackageFormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = "Package name is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (parseFloat(formData.base_price) <= 0) newErrors.base_price = "Price must be greater than 0";
    if (formData.services_data.length === 0) newErrors.services_data = "At least one service is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(formData);
  };

  const handleChange = (field: keyof PackageFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleRemoveService = (index: number) => {
    setFormData(prev => ({
      ...prev,
      services_data: prev.services_data.filter((_, i) => i !== index),
    }));
    setRemoveServiceConfirm(null);
  };

  const handleAddServiceFromModal = (service: { service_id: number; vendor_id?: number; base_price: number; selling_price: number }) => {
    const serviceData = services.find(s => s.id === service.service_id);
    if (serviceData) {
      const newService: PackageService = {
        service_id: service.service_id,
        name: serviceData.name,
        selling_price: service.selling_price,
        base_price: service.base_price,
        vendor_id: service.vendor_id,
        unit_price: service.selling_price,
        cost_price: service.base_price,
      };
      setFormData(prev => ({ ...prev, services_data: [...prev.services_data, newService] }));
    }
    setIsAddServiceModalOpen(false);
  };

  const availableServices = services.filter(
    s => !formData.services_data.some(ps => ps.service_id === s.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="w-full h-full bg-white flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-stone-50 to-white flex-shrink-0">
          <h2 className="text-xl font-bold text-stone-800">
            {initialData ? "Edit Package" : "Create New Package"}
          </h2>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700 flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Left Column - Basic Information */}
              <div className="space-y-6">
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-stone-700 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">1</span>
                    Basic Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1.5">Package Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="e.g., Premium Wedding Package"
                        className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                          errors.name
                            ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                            : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                        }`}
                      />
                      {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1.5">Package Price (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.base_price}
                          readOnly
                          placeholder="0"
                          className={`w-full px-3 py-2 rounded-lg border text-sm outline-none bg-stone-50 cursor-default ${
                            errors.base_price ? "border-rose-300" : "border-stone-200"
                          }`}
                        />
                        <p className="text-xs text-stone-400 mt-1">Auto from services</p>
                        {errors.base_price && <p className="text-xs text-rose-500 mt-1">{errors.base_price}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-stone-600 mb-1.5">Selling Price (₹) *</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.offered_price}
                          readOnly
                          placeholder="0"
                          className={`w-full px-3 py-2 rounded-lg border text-sm outline-none bg-stone-50 cursor-default ${
                            errors.offered_price ? "border-rose-300" : "border-stone-200"
                          }`}
                        />
                        <p className="text-xs text-stone-400 mt-1">Same as package price</p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1.5">Description *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        placeholder="Describe this package..."
                        rows={4}
                        className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none resize-none ${
                          errors.description
                            ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                            : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                        }`}
                      />
                      {errors.description && <p className="text-xs text-rose-500 mt-1">{errors.description}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Services */}
              <div className="space-y-6">
                <div className="bg-white border border-stone-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">2</span>
                      Services ({formData.services_data.length})
                    </h3>
                    {availableServices.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setIsAddServiceModalOpen(true)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-semibold transition-colors"
                      >
                        + Add Service
                      </button>
                    )}
                  </div>

                  {/* Services subtotal chips */}
                  {formData.services_data.length > 0 && (() => {
                    const costSum = formData.services_data.reduce((sum, s: any) => sum + toNumber(s.base_price ?? s.cost_price), 0);
                    const sellSum = formData.services_data.reduce((sum, s: any) => sum + toNumber(s.selling_price ?? s.unit_price), 0);
                    return (
                      <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2">
                          <p className="text-stone-500">Total Cost</p>
                          <p className="font-semibold text-stone-700">₹{Math.round(costSum).toLocaleString('en-IN')}</p>
                        </div>
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <p className="text-amber-700">Total Price</p>
                          <p className="font-semibold text-amber-800">₹{Math.round(sellSum).toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    );
                  })()}

                  {formData.services_data.length === 0 ? (
                    <div className="text-center py-8 bg-stone-50 rounded-lg border border-dashed border-stone-300">
                      <p className="text-sm text-stone-400">No services added yet</p>
                      <p className="text-xs text-stone-400 mt-1">Click "Add Service" to add services</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0">
                          <tr className="border-b border-stone-200 bg-stone-50">
                            <th className="text-left px-3 py-2.5 font-semibold text-stone-700">Service</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-stone-700 w-24">Cost</th>
                            <th className="text-right px-3 py-2.5 font-semibold text-stone-700 w-24">Price</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-stone-700 w-16">Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.services_data.map((service, index) => {
                            const basePrice = toNumber(service.base_price ?? service.cost_price);
                            const sellingPrice = toNumber(service.selling_price ?? service.unit_price);
                            return (
                              <tr key={index} className="border-b border-stone-100 hover:bg-amber-50/30 transition-colors">
                                <td className="px-3 py-2.5">
                                  <p className="font-medium text-stone-700 text-xs">{service.name}</p>
                                  {service.vendor_id && (
                                    <p className="text-xs text-stone-500">
                                      {vendors.find(v => v.id === service.vendor_id)?.name || 'Unknown'}
                                    </p>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-right font-medium text-stone-600 text-xs">
                                  ₹{Math.round(basePrice).toLocaleString('en-IN')}
                                </td>
                                <td className="px-3 py-2.5 text-right font-semibold text-stone-700 text-xs">
                                  ₹{Math.round(sellingPrice).toLocaleString('en-IN')}
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setRemoveServiceConfirm(index)}
                                    className="px-2 py-1 text-xs rounded bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-6 py-3 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-lg text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
          >
            {initialData ? "Update Package" : "Create Package"}
          </button>
        </div>

        {/* Remove Service Confirmation */}
        {removeServiceConfirm !== null && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
              <h3 className="text-lg font-bold text-stone-800 mb-2">Remove Service?</h3>
              <p className="text-sm text-stone-600 mb-6">
                Are you sure you want to remove <strong>{formData.services_data[removeServiceConfirm].name}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setRemoveServiceConfirm(null)}
                  className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-600 font-semibold text-sm hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveService(removeServiceConfirm)}
                  className="flex-1 px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold text-sm hover:bg-rose-700 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Service Modal */}
        <AddServiceModal
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          onAdd={handleAddServiceFromModal}
          services={availableServices}
          vendors={vendors}
          existingServiceIds={formData.services_data.map(s => s.service_id)}
          isAdmin={true}
        />
      </div>
    </div>
  );
}
